/**
 * useTheGraphAggregated — extracts the wallet-aggregation logic that
 * used to live inline in `TheGraphMonitor.tsx`'s render body.
 *
 * Returns both the raw `walletsData` query result and the aggregated
 * totals (native + USD). Cached via react-query (staleTime 2 min).
 *
 * Used by:
 *  - the `<TheGraphBalanceCell>` in `/networks/compare` Phase 4 — only
 *    consumes `totalValueUsd` + `isLoading` + `error`.
 *  - `TheGraphMonitor.tsx` itself (Phase 5 adoption) — consumes the
 *    full shape (raw walletsData, every native + USD total, refetch).
 */

import { useQuery } from '@tanstack/react-query';
import { thegraphAPI } from '../services/thegraphApi';
import type { TheGraphWalletData } from '../types/thegraph.types';

export interface TheGraphAggregated {
  /** Raw query result — `[]` while loading or on error. */
  walletsData: TheGraphWalletData[];
  /** Native GRT delegated. */
  totalDelegated: number;
  /** USD value of `totalDelegated`. */
  totalDelegatedUsd: number;
  /** Native GRT pending rewards. */
  pendingRewards: number;
  /** USD value of `pendingRewards`. */
  pendingRewardsUsd: number;
  /** Native GRT realized rewards. */
  realizedRewards: number;
  /** USD value of `realizedRewards`. */
  realizedRewardsUsd: number;
  /** Convenience sum (delegated + pending) in USD — what the compare
   *  page shows in its "Live Value" column. */
  totalValueUsd: number;
  /** Count of delegations across all the user's wallets. */
  delegationsCount: number;
  /** True while the underlying query is in-flight. */
  isLoading: boolean;
  /** Error from the underlying query, if any. */
  error: Error | null;
  /** Refetch the underlying query — exposed so the page Refresh button
   *  can re-trigger a fetch. */
  refetch: () => void;
}

const EMPTY: Omit<TheGraphAggregated, 'refetch'> = {
  walletsData: [],
  totalDelegated: 0,
  totalDelegatedUsd: 0,
  pendingRewards: 0,
  pendingRewardsUsd: 0,
  realizedRewards: 0,
  realizedRewardsUsd: 0,
  totalValueUsd: 0,
  delegationsCount: 0,
  isLoading: false,
  error: null,
};

export function useTheGraphAggregated(wallets: string[]): TheGraphAggregated {
  const { data: walletsData = [], isLoading, error, refetch } = useQuery({
    queryKey: ['graph-wallets-data', wallets],
    queryFn: async () => {
      const results = await Promise.all(wallets.map((w) => thegraphAPI.getWalletData(w)));
      return results.filter((r): r is TheGraphWalletData => r !== null);
    },
    enabled: wallets.length > 0,
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  if (wallets.length === 0) {
    return { ...EMPTY, refetch };
  }

  const totalDelegated = walletsData.reduce((sum, w) => sum + w.totalDelegated, 0);
  const totalDelegatedUsd = walletsData.reduce((sum, w) => sum + w.totalDelegatedUsd, 0);
  const pendingRewards = walletsData.reduce((sum, w) => sum + w.pendingRewards, 0);
  const pendingRewardsUsd = walletsData.reduce((sum, w) => sum + w.pendingRewardsUsd, 0);
  const realizedRewards = walletsData.reduce((sum, w) => sum + w.totalRealizedRewards, 0);
  const realizedRewardsUsd = walletsData.reduce((sum, w) => sum + w.totalRealizedRewardsUsd, 0);
  const delegationsCount = walletsData.reduce((sum, w) => sum + w.delegationsCount, 0);

  return {
    walletsData,
    totalDelegated,
    totalDelegatedUsd,
    pendingRewards,
    pendingRewardsUsd,
    realizedRewards,
    realizedRewardsUsd,
    totalValueUsd: totalDelegatedUsd + pendingRewardsUsd,
    delegationsCount,
    isLoading,
    error: (error as Error | null) ?? null,
    refetch,
  };
}
